import { Inject, Injectable, NgZone } from '@angular/core';
import { takeWhile } from 'rxjs/operators';
import { toError } from 'src/app/common/logging/error-normalization';

import makeDebug from '../../../../makeDebug';
import { AuthService } from '../../services/auth.service';
import { ConnectionStateService } from '../../services/connection-state.service';
import { IConnectionStateService } from '../../services/contracts/sync/connection-state-service';
import { IFeathersService } from '../../services/contracts/sync/feathers-service';
import { ISyncService } from '../../services/contracts/sync/service/sync-service';
import { FeathersService } from '../../services/feathers.service';
import { SyncTimeoutRepeaterServiceFactory } from '../../services/sync-timeout-repeater-factory..service';
import { MaintenanceSyncService } from '../../services/sync/maintenance-sync.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { MaintenanceSynchroniser as Synchroniser } from '../contracts/sync/sync-endpoints';

const debug = makeDebug('sync:maintenance-synchroniser');

@Injectable()
export class MaintenanceSynchroniser implements ISyncElement {
  public get name(): string {
    return Synchroniser;
  }
  private _isRunning = false;

  constructor(
    @Inject(MaintenanceSyncService) private _maintenanceSyncService: ISyncService,
    private _ngZone: NgZone,
    @Inject(FeathersService) private _feathersService: IFeathersService,
    private _authenticationService: AuthService,
    private _syncTimeoutRepeaterServiceFactory: SyncTimeoutRepeaterServiceFactory,
    @Inject(ConnectionStateService) private _connectionStateService: IConnectionStateService
  ) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('entered execute function', context.params.isOnline);
    const result: SyncElementResult = { contextId: context.id, jobDone: false };
    if (!this._connectionStateService.getSocketConnectionState() || this._isRunning) {
      result.connected = false;
      return result;
    }
    result.connected = true;

    this._isRunning = true;

    await new Promise<SyncElementResult>(resolve => {
      this._authenticationService.authenticatedEventPublisher
        .pipe(takeWhile(authEventData => !authEventData.isAuthenticated))
        .subscribe({
          complete: async () => {
            try {
              result['authenticatedEventPublisherCompleted'] = true;
              await this._maintenanceSyncService.setup(this._feathersService);
              debug('calling sync of sync-timeout-repeater', context.params.maintenanceToken);

              const syncTimeoutRepeater = this._syncTimeoutRepeaterServiceFactory.create(
                this._maintenanceSyncService,
                this._ngZone,
                null
              );

              const account = await this._authenticationService.getAccount();
              // This promise lives on the cancellation token: an unhandled rejection here
              // used to surface much later, misattributed to the TokenHandler step and
              // without any content (work item 27022). Log it at its origin instead.
              context.params.maintenanceToken.promise = syncTimeoutRepeater
                .sync(context.params.maintenanceToken, account)
                .catch(error => window.logger.error('Maintenance sync failed', toError(error, 'Maintenance sync failed')));

              result.jobDone = true;
            } catch (error) {
              // A throw here used to reject unhandled inside the RxJS callback and left the
              // outer promise pending — the pipeline then hung until the 15-minute timeout.
              window.logger.error(
                'MaintenanceSynchroniser failed to start maintenance sync',
                toError(error, 'MaintenanceSynchroniser failed to start maintenance sync')
              );
            } finally {
              this._isRunning = false;
              resolve(result);
            }
          },
          error: error => {
            // The auth event stream never errors today (BehaviorSubject fed by next() only),
            // but if it ever does — e.g. the takeWhile predicate throwing — complete() never
            // fires: without this handler the outer promise stayed pending, _isRunning stuck
            // and the pipeline hung until the 15-minute timeout.
            window.logger.error(
              'MaintenanceSynchroniser failed to start maintenance sync',
              toError(error, 'MaintenanceSynchroniser failed to start maintenance sync')
            );
            this._isRunning = false;
            resolve(result);
          },
        });
    });

    return result;
  }
}
